import React, { useMemo } from 'react';

// The administration default for step_window_ms, which decays 5000 -> 800 as the
// ladder climbs. Used only when the server omits the parameter.
const DEFAULT_WINDOW_MS = 5000;

// Used only when the served fieldSize is missing or unusable. VIEW / 0 is
// Infinity, which puts every node at NaN and draws an empty field — a broken
// parameter must degrade to a playable grid, not to a blank screen.
const DEFAULT_FIELD_SIZE = 6;

// The field is drawn in a fixed square user space and scaled by the viewBox, so
// no geometry here depends on the rendered pixel size.
const VIEW = 100;

// Renders the whole field once and keeps it mounted for the entire block. The
// field is generated ONCE per block and every trial serves the identical nodes
// array; clearing or regenerating it between steps would turn a sequenced
// search into a series of unrelated single searches.
//
// Nothing on screen depends on which step is being asked for. stimulus.stepIndex
// is served and the alternation rule is public, so highlighting the next node —
// or dimming the ones behind it — would remove the sequencing the instrument
// measures while leaving the score looking entirely normal.
//
// No completed-path line is drawn either. The client cannot know whether a tap
// was correct — the server does not say, and the trail does not advance on a
// wrong tap — so any path drawn from taps would be showing the participant an
// error signal the server never gave.
function StimulusView({ stimulus, disabled, onFirstInteraction, onCommit }) {
  const size = Number(stimulus?.fieldSize) || DEFAULT_FIELD_SIZE;
  const nodes = stimulus?.nodes;
  const step = VIEW / size;

  // Served (row, col) verbatim. The generator shuffles slots precisely so the
  // next target is not reliably the nearest unvisited node; re-laying out,
  // sorting or "spreading out" the field would undo that and hand the
  // participant a nearest-neighbour strategy instead of a visual search.
  const laidOut = useMemo(
    () =>
      (nodes || []).map((n) => ({
        label: n.label,
        cx: Number(n.col) * step + step / 2,
        cy: Number(n.row) * step + step / 2,
      })),
    [nodes, step]
  );

  // One tap is the whole answer, so the first interaction and the commit are the
  // same event. beginComposite must still be called first — it is what stamps
  // the reaction time, and commitComposite is a no-op without it. Calling them
  // in this order is also what keeps the response trio all-or-nothing: an
  // anticipation refuses the stamp, and the commit that follows is then refused
  // too, leaving a clean non-response rather than a partial trio the server
  // 400s the whole block for.
  //
  // Every node stays tappable, including ones already used and ones that are
  // wrong. Refusing a tap would leak which node is next and would also hide the
  // error from the server, which is the only party that grades it.
  const tap = (label) => {
    if (disabled) return;
    onFirstInteraction();
    onCommit({ label });
  };

  return (
    <svg
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="group"
      aria-label="Trail field"
      style={{
        width: 'min(78vw, 78vh)',
        height: 'min(78vw, 78vh)',
        maxWidth: '100%',
        touchAction: 'manipulation',
      }}
    >
      {laidOut.map((n) => (
        <g
          key={n.label}
          role="button"
          tabIndex={disabled ? -1 : 0}
          // Names the node by the label already printed inside it, and by
          // nothing else. A name carrying "next" or a step number would read
          // the answer out to exactly the participants who cannot see the
          // field.
          aria-label={`Node ${n.label}`}
          onClick={() => tap(n.label)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              tap(n.label);
            }
          }}
          style={{ cursor: disabled ? 'default' : 'pointer' }}
        >
          <circle
            cx={n.cx}
            cy={n.cy}
            r={step * 0.42}
            fill="var(--bg-elevated)"
            stroke="var(--border-strong)"
            strokeWidth="0.8"
          />
          <text
            x={n.cx}
            y={n.cy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={step * 0.42}
            fontWeight="700"
            fill="var(--text-primary)"
          >
            {n.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

export const trailMakingTask = {
  id: 'trail-making',
  name: 'Trail',
  pillar: 'flexibility',

  // Composite: the trial ends at commitComposite regardless, so the driver's
  // early-advance timer never applies here. Declared anyway — registry.test.js
  // requires an explicit pacing declaration from every module.
  holdsFullWindow: false,

  instructions: [
    'Tap the circles in order, alternating numbers and letters:',
    '1 → A → 2 → B → 3 → C and so on.',
    'Each step is timed from your last tap, so keep moving.',
  ],

  // The field IS the response surface, so there are no button targets.
  responseMode: 'composite',
  targets: [],

  // One field for the whole block, each step timed from the previous tap. The
  // runner skips fixation and the inter-trial gap and stamps step i's onset at
  // step i-1's response, so no dead time lands inside a measured interval.
  chainOnset: true,

  // TaskExamples renders one stimulus per row, and a single frame of this task
  // is a field of circles that illustrates nothing the instructions do not
  // already say. The lesson is the ORDER, which no static frame can show.
  examples: [],

  // Never reached: a composite task builds its own answer object.
  toAnswer: () => ({}),

  // step_window_ms is the per-step window and the server marks a later tap
  // INCORRECT, so it is part of grading rather than only a diagnostic.
  // node_count is the other difficulty knob and is already visible as the field
  // itself, so there is nothing to hide.
  //
  // Coerced through Number() because the runner derives its reaction-time
  // ceiling as `windowMs + 250`: a string window would concatenate
  // ("1800" + 250 -> "1800250") and every late tap would be accepted as a real
  // response. `||` rather than `??` so a served 0 falls back instead of timing
  // every step out before the field is even painted.
  timing: (params) => ({
    fixationMs: 0,
    windowMs: Number(params?.step_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: 0,
  }),

  // The rule is public and the alternation is deterministic, so the expected
  // label is computable with nothing hidden: stepIndex is served. Returned as
  // the answer OBJECT, not a bare label — the runner compares a composite
  // expectation against the committed answer with JSON.stringify, and this
  // task's answer is {label}.
  practiceExpectation: (stimulus) => {
    const step = Number(stimulus?.stepIndex);
    if (!Number.isInteger(step) || step < 0) return null;
    return { label: labelAt(step) };
  },

  StimulusView,
};

// 1, A, 2, B, 3, C… Numbers on even steps, letters on odd ones. Mirrors the
// server's own labelling, which is what makes the trail readable.
export function labelAt(step) {
  return step % 2 === 0
    ? String(step / 2 + 1)
    : String.fromCharCode('A'.charCodeAt(0) + Math.floor(step / 2));
}
